/**
 * Turns a failure into a sentence a person can act on.
 *
 * This file reads the facts off whatever was thrown (`FailureFacts`) and
 * `failure-words.ts` picks the sentence: what went wrong and what to do
 * about it, never a status code, a command or a host. The developer's detail
 * (the endpoint, the status, the server's raw words) goes to the console in
 * development only.
 */
import { AUTH_BASE_PATH } from '@alluzo/shared';

import { apiBaseUrl } from '@/lib/api';
import { failureWordsOf, type FailureFacts } from '@/lib/failure-words';
import { missingRow } from '@/lib/missing-row';
import { isTransportFailure } from '@/lib/sync-failure';
import { asTeamRefusal, type TeamOutcome } from '@/lib/team-data';

/** The shape Better Auth's client resolves with when a call is refused. */
type FailureLike = {
  code?: unknown;
  message?: unknown;
  status?: unknown;
};

const asFailure = (value: unknown): FailureLike =>
  typeof value === 'object' && value !== null ? (value as FailureLike) : {};

const asText = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;

const asStatus = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/**
 * The developer's line, development only. A 404 with no envelope means the
 * running server lacks the route: almost always an old process, so the line
 * says to restart it.
 */
const tellDeveloper = (endpoint: string, facts: FailureFacts): void => {
  if (typeof __DEV__ === 'undefined' || !__DEV__) return;
  const { status, code, message } = facts;
  const advice =
    status === 404 && code === null
      ? 'The server has no such route: probably an old process. Restart it with `bun run server`.'
      : undefined;
  console.warn('[failure]', { endpoint, status, code, message, advice });
};

/**
 * For a Better Auth call: `signIn.email`, `signUp.email`, `signOut`.
 *
 * Accepts either the `error` the client resolves with, or a thrown value, so a
 * caller can hand over whichever it got without inspecting it first.
 */
export const describeAuthFailure = (failure: unknown): string => {
  const endpoint = `${apiBaseUrl}${AUTH_BASE_PATH}`;
  const { code, message, status } = asFailure(failure);
  const held = asStatus(status);
  const facts: FailureFacts = {
    /* A rejected fetch: there was no HTTP response at all. */
    transport: (failure instanceof Error && held === null) || held === 0,
    status: held === 0 ? null : held,
    code: asText(code),
    message: asText(message),
    own: null,
    auth: true,
  };
  tellDeveloper(endpoint, facts);
  return failureWordsOf(facts);
};

/** For a call to our own `/v1` API. */
export const describeApiFailure = (failure: unknown): string => {
  const endpoint = `${apiBaseUrl}/v1`;
  const { code, message, status } = asFailure(failure);
  const held = asStatus(status);
  const transport = held === 0 || (held === null && isTransportFailure(failure));
  // An error this app raised for itself. Its own sentence is the true one,
  // and inventing a network story over it sends the person to the wrong fix.
  const own = held === null && !transport && failure instanceof Error ? asText(failure.message) : null;
  // A 404 the route answered in its own envelope is a row it does not have,
  // never a route it lacks: keep the code only when the envelope is whole.
  const codeText = asText(code);
  const facts: FailureFacts = {
    transport,
    status: held === 0 ? null : held,
    code: held === 404 && missingRow(failure) === null ? null : codeText,
    message: asText(message),
    own,
    auth: false,
  };
  tellDeveloper(endpoint, facts);
  return failureWordsOf(facts);
};

/**
 * Run one team mutator and answer BOTH endings as a value.
 *
 * Why this lives here rather than in each hook: it is the same question the
 * two functions above answer — what does a person see when this failed — and
 * the answer for a mutator has one more branch than a sentence. A refusal a
 * person can act on ("This is the only owner of the business") belongs beside
 * the button that provoked it, with the server's own code so a screen can
 * highlight the field. Everything else is a `failed`, with the sentence
 * `describeApiFailure` writes.
 *
 * Nothing here throws, and that is the point. A rejected promise out of an
 * `onPress` is an unhandled rejection, and the one screen that forgets to catch
 * it takes the list behind the button down with it. A 409 on Save must never
 * unmount a member list.
 *
 * `asTeamRefusal` decides which codes qualify; see `team-data.ts` for the list
 * and why each one is on it or not.
 */
export const teamOutcome = async <T,>(call: () => Promise<T>): Promise<TeamOutcome<T>> => {
  try {
    return { kind: 'done', value: await call() };
  } catch (thrown) {
    const refusal = asTeamRefusal(thrown);
    return refusal === null
      ? { kind: 'failed', message: describeApiFailure(thrown) }
      : { kind: 'refused', code: refusal.code, message: refusal.message };
  }
};
